import { useEffect, useState } from "react";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import { Col, Modal, Row } from "react-bootstrap";
import { BrowserView, MobileView } from "react-device-detect";
import { FaTimes, FaCheckCircle } from "react-icons/fa";
import { Center } from "./Center";
import { SizedBox } from "./SizedBox";
import { TitleText } from "./TitleText";
import { DescriptionText } from "./DescriptionText";
import { PrimaryButton } from "./PrimaryButton";
import { FlatButton } from "./FlatButton";
import IconButton from "./IconButton";
import { StandardShadow } from "./StandardShadow";
import { useAppColors } from "../utils/useAppColors";

function shorten(addr) {
  if (!addr) return "";
  return addr.length > 12 ? addr.slice(0, 5) + "..." + addr.slice(-4) : addr;
}

function label(a) {
  if (!a) return "Account";
  if (a.username) return a.username;
  if (a.displayName) return a.displayName;
  if (a.evmAddress) return shorten(a.evmAddress);
  if (a.solanaAddress) return shorten(a.solanaAddress);
  return "Account";
}

function subLabel(a) {
  if (!a) return "";
  const parts = [];
  if (a.evmAddress) parts.push("ETH " + shorten(a.evmAddress));
  if (a.solanaAddress) parts.push("SOL " + shorten(a.solanaAddress));
  if (a.email) parts.push(a.email);
  return parts.join("  •  ");
}

// Shown when a connected Gmail already belongs to another account. Lets the
// user pick which account survives; the other folds into it and is deactivated.
export default function MergeAccountModal({
  open,
  setOpen,
  email,
  currentAccount,
  otherAccount,
  onMerge,
  isLoading,
}) {
  const colors = useAppColors();
  const [survivorId, setSurvivorId] = useState(undefined);

  useEffect(() => {
    if (currentAccount && currentAccount._id) {
      setSurvivorId(currentAccount._id);
    }
  }, [currentAccount]);

  function AccountCard({ account }) {
    if (!account) return null;
    const selected = survivorId === account._id;
    return (
      <StandardShadow borderRadius={"18px"}>
        <div
          onClick={() => setSurvivorId(account._id)}
          style={{
            cursor: "pointer",
            border: `2px solid ${selected ? colors.primaryColor : "#ddd"}`,
            borderRadius: "18px",
            padding: "16px",
            marginBottom: "12px",
            background: "white",
          }}
        >
          <Row className="d-flex align-items-center">
            <Col>
              <TitleText textAlign={"start"} fontSize={"18px"}>
                {label(account)}
              </TitleText>
              <DescriptionText textAlign={"start"}>
                {subLabel(account)}
              </DescriptionText>
            </Col>
            <Col xs={"auto"}>
              {selected && (
                <FaCheckCircle size={22} color={colors.primaryColor} />
              )}
            </Col>
          </Row>
        </div>
      </StandardShadow>
    );
  }

  const body = (
    <div style={{ width: "90%" }}>
      <SizedBox height={8} />
      <TitleText textAlign={"start"}>Merge accounts</TitleText>
      <SizedBox height={8} />
      <DescriptionText textAlign={"start"}>
        {email
          ? `${email} already belongs to another account. Pick the account to keep — the other's wallets, bonds, and subscription move into it and it's deactivated.`
          : "This email already belongs to another account. Pick the account to keep."}
      </DescriptionText>
      <SizedBox height={20} />
      <DescriptionText textAlign={"start"}>Keep this account:</DescriptionText>
      <SizedBox height={8} />
      <AccountCard account={currentAccount} />
      <AccountCard account={otherAccount} />
      <SizedBox height={16} />
      <PrimaryButton
        text="Merge"
        isLoading={isLoading}
        disabled={!survivorId}
        onClick={() => onMerge(survivorId)}
      />
      <SizedBox height={12} />
      <Row>
        <FlatButton onClick={() => setOpen(false)}>Cancel</FlatButton>
      </Row>
    </div>
  );

  return (
    <>
      <BrowserView>
        <Modal
          dialogClassName="modal-60w"
          show={open}
          onHide={() => setOpen(false)}
        >
          <Modal.Body>
            <Center>
              <div style={{ width: "90%" }}>
                <SizedBox height={8} />
                <Row className="d-flex align-items-end justify-content-end">
                  <Col className="d-flex align-items-end justify-content-end">
                    <IconButton Icon={FaTimes} onClick={() => setOpen(false)} />
                  </Col>
                </Row>
                {body}
              </div>
            </Center>
          </Modal.Body>
        </Modal>
      </BrowserView>
      <MobileView>
        <BottomSheet open={open} onDismiss={() => setOpen(false)}>
          <Center>{body}</Center>
          <SizedBox height={32} />
        </BottomSheet>
      </MobileView>
    </>
  );
}
